import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { Theme, ThemeConfig, CSSCustomProperties } from '../tokens';
import { generateCSSCustomProperties } from '../utils/generateCSSVars';
import { applyThemeToDocument } from '../utils/themeHelpers';

// Theme context interface
export interface ThemeContextValue {
  // Current theme
  currentTheme: Theme;
  
  // Available themes
  availableThemes: Theme[];
  
  // Theme switching
  setTheme: (themeId: string) => void;
  
  // Theme configuration
  config: ThemeConfig;
  
  // System preference detection
  systemPreference: 'light' | 'dark' | null;
  
  // Loading state
  isLoading: boolean;
  
  // Error state
  error: string | null;
  
  // CSS custom properties for current theme
  cssCustomProperties: CSSCustomProperties;
}

// Create the theme context
const ThemeContext = createContext<ThemeContextValue | null>(null);

// Theme provider props
export interface ThemeProviderProps {
  children: ReactNode;
  themes: Theme[];
  config?: Partial<ThemeConfig>;
  defaultTheme?: string;
}

// Default theme configuration
const defaultConfig: ThemeConfig = {
  defaultTheme: 'dark',
  availableThemes: ['dark', 'light'],
  storageKey: 'daggerheart-theme',
  enableSystemPreference: true,
  enableTransitions: true,
  transitionDuration: 300,
};

// System preference detection hook
const useSystemPreference = (): 'light' | 'dark' | null => {
  const [preference, setPreference] = useState<'light' | 'dark' | null>(null);

  useEffect(() => {
    // Check if the browser supports prefers-color-scheme
    if (!window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Set initial preference
    setPreference(mediaQuery.matches ? 'dark' : 'light');

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPreference(event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return preference;
};

// Local storage helpers
const getStoredTheme = (storageKey: string): string | null => {
  try {
    return localStorage.getItem(storageKey);
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
    return null;
  }
};

const setStoredTheme = (storageKey: string, themeId: string): void => {
  try {
    localStorage.setItem(storageKey, themeId);
  } catch (error) {
    console.warn('Failed to store theme in localStorage:', error);
  }
};

// Main theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  themes,
  config: userConfig,
  defaultTheme,
}) => {
  // Merge user config with defaults
  const config = useMemo(
    () => ({ ...defaultConfig, ...userConfig, defaultTheme: defaultTheme || defaultConfig.defaultTheme }),
    [userConfig, defaultTheme]
  );

  // System preference detection
  const systemPreference = useSystemPreference();

  // Theme state
  const [currentThemeId, setCurrentThemeId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get current theme object
  const currentTheme = useMemo(() => {
    const theme = themes.find(t => t.metadata.name === currentThemeId);
    if (!theme) {
      const fallback = themes.find(t => t.metadata.name === config.defaultTheme) || themes[0];
      return fallback;
    }
    return theme;
  }, [themes, currentThemeId, config.defaultTheme]);

  // Generate CSS custom properties for current theme
  const cssCustomProperties = useMemo(() => {
    if (!currentTheme) return {};
    try {
      return generateCSSCustomProperties(currentTheme.tokens);
    } catch (err) {
      console.error('Failed to generate CSS custom properties:', err);
      setError('Failed to generate theme styles');
      return {};
    }
  }, [currentTheme]);

  // Initialize theme on mount
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    try {
      // Priority order for theme selection:
      // 1. Stored theme preference
      // 2. System preference (if enabled)
      // 3. Default theme from config
      
      const storedTheme = getStoredTheme(config.storageKey);
      let initialTheme = config.defaultTheme;

      if (storedTheme && themes.some(t => t.metadata.name === storedTheme)) {
        initialTheme = storedTheme;
      } else if (config.enableSystemPreference && systemPreference) {
        // Find a theme that matches system preference
        const matchingTheme = themes.find(t => 
          (systemPreference === 'dark' && t.metadata.isDark) ||
          (systemPreference === 'light' && !t.metadata.isDark)
        );
        if (matchingTheme) {
          initialTheme = matchingTheme.metadata.name;
        }
      }

      setCurrentThemeId(initialTheme);
    } catch (err) {
      console.error('Failed to initialize theme:', err);
      setError('Failed to initialize theme');
      setCurrentThemeId(config.defaultTheme);
    } finally {
      setIsLoading(false);
    }
  }, [themes, config, systemPreference]);

  // Apply theme to document when current theme changes
  useEffect(() => {
    if (!currentTheme || isLoading) return;

    try {
      applyThemeToDocument(cssCustomProperties, config);
    } catch (err) {
      console.error('Failed to apply theme to document:', err);
      setError('Failed to apply theme');
    }
  }, [currentTheme, cssCustomProperties, config, isLoading]);

  // Theme switching function
  const setTheme = (themeId: string) => {
    if (!themes.some(t => t.metadata.name === themeId)) {
      console.warn(`Theme "${themeId}" not found. Available themes:`, themes.map(t => t.metadata.name));
      return;
    }

    setCurrentThemeId(themeId);
    setStoredTheme(config.storageKey, themeId);
  };

  // Context value
  const contextValue: ThemeContextValue = {
    currentTheme,
    availableThemes: themes,
    setTheme,
    config,
    systemPreference,
    isLoading,
    error,
    cssCustomProperties,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme context
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

// Hook to get theme tokens with TypeScript support
export const useThemeTokens = () => {
  const { currentTheme } = useTheme();
  return currentTheme.tokens;
};

// Hook to get specific token category
export const usePrimitiveTokens = () => {
  const { currentTheme } = useTheme();
  return currentTheme.tokens.primitive;
};

export const useSemanticTokens = () => {
  const { currentTheme } = useTheme();
  return currentTheme.tokens.semantic;
};

// Hook for checking if current theme is dark
export const useIsDarkTheme = (): boolean => {
  const { currentTheme } = useTheme();
  return currentTheme.metadata.isDark;
};

// Hook for theme-aware conditional rendering
export const useThemeVariant = <T,>(darkValue: T, lightValue: T): T => {
  const isDark = useIsDarkTheme();
  return isDark ? darkValue : lightValue;
};
